import { apiClient } from './apiClient';

// Maps the generic collection names the pages already use onto real REST
// endpoints. Kept deliberately similar to the old Supabase-shaped calls so
// page components needed minimal changes.
const COLLECTION_ROUTES = {
  scholarships: '/scholarships',
  scholarship_applications: '/scholarships/applications/mine',
  mentoring_sessions: '/mentors',
  peer_sessions: '/peer-sessions',
  session_enrollments: null, // no direct listing endpoint — booking is via bookSession()
  library_resources: '/notes',
  communities: '/community/groups',
};

export const api = {
  async getCollection(tableName) {
    const route = COLLECTION_ROUTES[tableName];
    if (!route) throw new Error(`No collection route configured for "${tableName}"`);
    return apiClient.get(route);
  },

  async getWhere(tableName, column, value) {
    // Only meaningful usage in this app: "give me MY applications/enrollments".
    if (tableName === 'scholarship_applications' && column === 'user_id') {
      return apiClient.get('/scholarships/applications/mine');
    }
    if (tableName === 'session_enrollments' && column === 'user_id') {
      // Return enrolled session IDs for the current user
      return apiClient.get('/mentors/me/enrollments');
    }
    throw new Error(`getWhere("${tableName}", "${column}") is not supported by this backend`);
  },

  async getDocument(tableName, id) {
    if (tableName === 'mentoring_sessions') return apiClient.get(`/mentors/${id}`, { auth: false });
    throw new Error(`getDocument("${tableName}") is not supported by this backend`);
  },

  async createDocument(tableName, payload) {
    if (tableName === 'scholarships') return apiClient.post('/scholarships', payload);
    if (tableName === 'scholarship_applications') {
      return apiClient.post(`/scholarships/${payload.scholarship_id}/apply`, { supportRequested: !!payload.supportRequested });
    }
    throw new Error(`createDocument("${tableName}") is not supported by this backend`);
  },

  async updateDocument(tableName, id, payload) {
    if (tableName === 'scholarships') return apiClient.patch(`/scholarships/${id}`, payload);
    if (tableName === 'mentoring_sessions') return apiClient.patch(`/mentors/${id}`, payload);
    if (tableName === 'scholarship_applications') return apiClient.patch(`/scholarships/applications/${id}/status`, payload);
    throw new Error(`updateDocument("${tableName}") is not supported by this backend`);
  },

  async deleteDocument(tableName, id) {
    if (tableName === 'scholarships') return apiClient.delete(`/scholarships/${id}`);
    throw new Error(`deleteDocument("${tableName}") is not supported by this backend`);
  },

  async updateScholarshipApplicationStatus(applicationId, status) {
    return apiClient.patch(`/scholarships/applications/${applicationId}/status`, { status });
  },

  async getMyScholarshipApplications() {
    return apiClient.get('/scholarships/applications/mine');
  },

  // ---- Activity logging ----

  async logActivity({ skill, activityType, durationSeconds, refId, quizAccuracy, applicationType, focusMode, focusDepth, sessionCompleted }) {
    return apiClient.post('/activity', {
      skill,
      activityType,
      durationSeconds,
      refId,
      quizAccuracy,
      applicationType,
      focusMode,
      focusDepthSelfReport: focusDepth,
      sessionCompleted,
    });
  },


  async getMyDashboard() {
    return apiClient.get('/dashboard/me');
  },

  async mentorValidateActivity(activityId) {
    return apiClient.post(`/activity/${activityId}/mentor-validate`, {});
  },

  async rateActivity(activityId, rating, kind) {
    return apiClient.post(`/activity/${activityId}/rate`, { rating, kind });
  },

  async bookSession(sessionId) {
    return apiClient.post(`/mentors/${sessionId}/sessions`, {});
  },

  async requestOtp(email, purpose) {
    return apiClient.post('/auth/otp/request', { email, purpose }, { auth: false });
  },

  async createPeerSession(payload) {
    return apiClient.post('/peer-sessions', payload);
  },

  async joinPeerSession(sessionId) {
    return apiClient.post(`/peer-sessions/${sessionId}/join`, {});
  },

  async startPeerSession(sessionId) {
    return apiClient.patch(`/peer-sessions/${sessionId}/start`, {});
  },

  async endPeerSession(sessionId) {
    return apiClient.patch(`/peer-sessions/${sessionId}/end`, {});
  },

  async ratePeerSession(sessionId, rating, comment) {
    return apiClient.post(`/peer-sessions/${sessionId}/rate`, { rating, comment });
  },

  async getPeerSessionAnalytics(sessionId) {
    return apiClient.get(`/peer-sessions/${sessionId}/analytics`);
  },

  async startMentorSession(sessionId) {
    return apiClient.patch(`/mentors/${sessionId}/start`, {});
  },

  async endMentorSession(sessionId) {
    return apiClient.patch(`/mentors/${sessionId}/end`, {});
  },

  async rateMentorSession(sessionId, rating, comment) {
    return apiClient.post(`/mentors/${sessionId}/rate`, { rating, comment });
  },

  async shareSessionNotes(sessionId, notes) {
    return apiClient.post(`/mentors/${sessionId}/notes`, { notes });
  },

  async getMyEnrolledSessionIds() {
    return apiClient.get('/mentors/me/enrollments');
  },

  async getMentorSessionAnalytics(sessionId) {
    return apiClient.get(`/mentors/${sessionId}/analytics`);
  },

  async adminListAllPeerSessions() {
    return apiClient.get('/admin/rooms/peer-sessions');
  },

  async adminDeletePeerSession(id) {
    return apiClient.delete(`/admin/rooms/peer-sessions/${id}`);
  },

  async adminListAllMentorSessions() {
    return apiClient.get('/admin/rooms/mentor-sessions');
  },

  async adminDeleteMentorSession(id) {
    return apiClient.delete(`/admin/rooms/mentor-sessions/${id}`);
  },

  async getMyAnnouncements() {
    return apiClient.get('/announcements/mine');
  },

  async adminListAnnouncements() {
    return apiClient.get('/admin/announcements');
  },

  async adminCreateAnnouncement(payload) {
    return apiClient.post('/admin/announcements', payload);
  },

  async adminUpdateAnnouncement(id, updates) {
    return apiClient.patch(`/admin/announcements/${id}`, updates);
  },

  async adminListPendingMentors() {
    return apiClient.get('/admin/mentors/pending');
  },

  async adminApproveMentor(userId) {
    return apiClient.patch(`/admin/mentors/${userId}/approve`, {});
  },

  async adminRejectMentor(userId, reason) {
    return apiClient.patch(`/admin/mentors/${userId}/reject`, { reason });
  },

  async adminGetEngineConfig() {
    return apiClient.get('/admin/engine-config');
  },

  async joinCommunity(communityId) {
    return apiClient.post(`/community/groups/${communityId}/join`, {});
  },

  // ---- admin ----

  async adminListUsers() {
    return apiClient.get('/admin/users');
  },

  async adminUpdateUserRole(userId, role) {
    return apiClient.patch(`/admin/users/${userId}`, { role });
  },

  // ---- certificates ----

  async verifyCertificate(code) {
    return apiClient.get(`/certificates/verify/${encodeURIComponent(code)}`, { auth: false });
  },
};
